import { useState, useEffect } from 'react'
import { router, useForm, usePage } from '@inertiajs/react'
import Layout from '@/pages/layouts/application'
import { PageHeader } from '@/components/core/page_header'
import postsApi from '@/api/Organizations/PostsApi'
import * as api from '@/api'
import 'react-calendar/dist/Calendar.css'

import PostCreationBlock from './components/post_creation_block'
import { useThread } from './hooks/thread'

export default function NewPost({ assets }) {
  const { organization, user } = usePage().props.auth
  const { integrations } = usePage().props

  const { data, setData, post, transform, processing, errors } = useForm({
    scheduled_at: null,
    file: null,
    asset_id: null,
    content: "",
  })

  // Separate thread state for each platform
  const { 
    posts: twitterPosts, 
    updatePost: updateTwitterPost, 
    addPost: addTwitterPost, 
    removePost: removeTwitterPost, 
    joinedContent: twitterJoinedContent 
  } = useThread("")
  
  const { 
    posts: linkedinPosts, 
    updatePost: updateLinkedinPost, 
    addPost: addLinkedinPost, 
    removePost: removeLinkedinPost, 
    joinedContent: linkedinJoinedContent 
  } = useThread("")

  const [crossPostEnabled, setCrossPostEnabled] = useState(true)

  // Wrapper functions that sync both platforms when cross-posting is enabled
  const handleUpdateTwitterPost = (index, content) => {
    updateTwitterPost(index, content)
    if (crossPostEnabled) {
      updateLinkedinPost(index, content)
    }
  }

  const handleUpdateLinkedinPost = (index, content) => {
    updateLinkedinPost(index, content)
    if (crossPostEnabled) {
      updateTwitterPost(index, content)
    }
  }

  const handleAddTwitterPost = () => {
    addTwitterPost()
    if (crossPostEnabled) {
      addLinkedinPost()
    }
  }

  const handleAddLinkedinPost = () => {
    addLinkedinPost()
    if (crossPostEnabled) {
      addTwitterPost()
    }
  }

  const handleRemoveTwitterPost = (index) => {
    removeTwitterPost(index)
    if (crossPostEnabled) {
      removeLinkedinPost(index)
    }
  }

  const handleRemoveLinkedinPost = (index) => {
    removeLinkedinPost(index)
    if (crossPostEnabled) {
      removeTwitterPost(index)
    }
  }

  const [mediaPreviewUrl, setMediaPreviewUrl] = useState(null)
  const [mediaType, setMediaType] = useState(null);
  const [replyContent, setReplyContent] = useState("")
  const [replyId, setReplyId] = useState(null)

  const handleReplyChange = (content) => {
    setReplyContent(content)
  }

  const handleSubmit = (e, selectedTargets) => {
    e.preventDefault()

    // Redirect to upgrade page if no free posts remaining
    if (!organization.subscribed && organization.free_posts_remaining <= 0) {
      router.visit(api.organizationsUpgrade.show.path({ org_nano_id: organization.nano_id }))
      return
    }

    const postPayload = {
      scheduled_at: data.scheduled_at,
      twitter_content: twitterJoinedContent,
      linkedin_content: linkedinJoinedContent,
      targets: selectedTargets,
    };

    if (selectedTargets.includes("linkedin") && replyContent.trim()) {
      postPayload.reply_attributes = {
        content: replyContent.trim(),
        organization_id: organization.id
      }
    }

    if (data.file) {
      postPayload.asset_attributes = { file: data.file };
    } else if (data.asset_id) {
      postPayload.asset_attachment_attributes = { asset_id: data.asset_id };
    }

    router.post(postsApi.create.path({ org_nano_id: organization.nano_id }), {
      post: postPayload
    }, {
      preserveScroll: true,
    })
  }

  const handlePublishNow = (e, selectedTargets) => {
    e.preventDefault()

    // Redirect to upgrade page if no free posts remaining
    if (!organization.subscribed && organization.free_posts_remaining <= 0) {
      router.visit(api.organizationsUpgrade.show.path({ org_nano_id: organization.nano_id }))
      return
    }

    const postPayload = {
      scheduled_at: new Date().toISOString(),
      twitter_content: twitterJoinedContent,
      linkedin_content: linkedinJoinedContent,
      targets: selectedTargets,
      publish_now: true,
    };

    if (selectedTargets.includes("linkedin") && replyContent.trim()) {
      postPayload.reply_attributes = {
        content: replyContent.trim(),
        organization_id: organization.id
      }
    }

    if (data.file) {
      postPayload.asset_attributes = { file: data.file };
    } else if (data.asset_id) {
      postPayload.asset_attachment_attributes = { asset_id: data.asset_id };
    }

    router.post(postsApi.create.path({ org_nano_id: organization.nano_id }), {
      post: postPayload
    }, {
      preserveScroll: true,
    })
  }

  const handleMediaChange = (mediaData, previewUrl, mediaType) => {
    if (mediaData?.file) {
      setData({ ...data, file: mediaData.file, asset_id: null });
    } else if (mediaData?.asset_id) {
      setData({ ...data, asset_id: mediaData.asset_id, file: null });
    } else {
      setData({ ...data, file: null, asset_id: null });
    }
    setMediaPreviewUrl(previewUrl);
    setMediaType(mediaType);
  };

  const handleNewDraft = () => {
    // Clear Twitter posts
    for (let i = twitterPosts.length - 1; i > 0; i -= 1) {
      removeTwitterPost(i)
    }
    updateTwitterPost(0, "")
    // Clear LinkedIn posts
    for (let i = linkedinPosts.length - 1; i > 0; i -= 1) {
      removeLinkedinPost(i)
    }
    updateLinkedinPost(0, "")
    setData({ ...data, scheduled_at: null, file: null, asset_id: null })
    setMediaPreviewUrl(null)
    setMediaType(null)
    setReplyContent("")
    setReplyId(null)
  }

  const isSubmitDisabled = processing || !data.scheduled_at || (
    twitterPosts.every((post) => !post.trim()) && linkedinPosts.every((post) => !post.trim())
  )

  const currentSelection = data.file
    ? { type: 'new', url: mediaPreviewUrl, mediaType }
    : data.asset_id
    ? { type: 'existing', id: data.asset_id, url: mediaPreviewUrl, mediaType }
    : null

  return (
    <Layout>
      <PostCreationBlock
        scheduledAt={data.scheduled_at}
        onScheduleChange={(newDate) => setData('scheduled_at', newDate)}
        onSubmit={handleSubmit}
        onPublishNow={handlePublishNow}
        onNewDraft={handleNewDraft}
        isSubmitDisabled={isSubmitDisabled}
        errors={errors}
        twitterPosts={twitterPosts}
        onUpdateTwitterPost={handleUpdateTwitterPost}
        onAddTwitterPost={handleAddTwitterPost}
        onRemoveTwitterPost={handleRemoveTwitterPost}
        linkedinPosts={linkedinPosts}
        onUpdateLinkedinPost={handleUpdateLinkedinPost}
        onAddLinkedinPost={handleAddLinkedinPost}
        onRemoveLinkedinPost={handleRemoveLinkedinPost}
        crossPostEnabled={crossPostEnabled}
        onCrossPostToggle={() => setCrossPostEnabled(prev => !prev)}
        assets={assets}
        onMediaChange={handleMediaChange}
        mediaSelection={currentSelection}
        organizationName={organization?.name}
        userEmail={user?.email}
        integrations={integrations}
        onReplyChange={handleReplyChange}
        organizationId={organization?.id}
      />
    </Layout>
  )
}
